import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CurrencyDollar, CursorClick, TrendUp, Lightning, ArrowsClockwise, Copy } from '@phosphor-icons/react';
import { useWebSocket } from '@/hooks/useWebSocket';
import LiveFeed from '@/components/shared/LiveFeed';
import { toast } from 'sonner';
import api from '@/lib/api';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 shadow-xl">
      <p className="text-xs text-zinc-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? (p.name === 'revenue' ? `$${p.value.toLocaleString()}` : p.value.toLocaleString()) : p.value}
        </p>
      ))}
    </div>
  );
};

export default function AffiliateDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { events, connected } = useWebSocket();

  useEffect(() => {
    api.get('/affiliate/dashboard').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const copyLink = async (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Link copied to clipboard');
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <p className="text-zinc-400">Failed to load dashboard.</p>;

  const { kpis, earnings_chart, smart_links } = data;

  return (
    <div className="space-y-6" data-testid="affiliate-dashboard">
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Affiliate Dashboard</h1>
        <p className="text-sm text-zinc-500 mt-1">Your performance at a glance</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { label: 'Total Earnings', value: `$${(kpis.total_earnings || 0).toLocaleString()}`, icon: CurrencyDollar, color: 'text-emerald-400' },
          { label: 'Total Clicks', value: (kpis.total_clicks || 0).toLocaleString(), icon: CursorClick, color: 'text-blue-400' },
          { label: 'Conversions', value: (kpis.total_conversions || 0).toLocaleString(), icon: TrendUp, color: 'text-emerald-400' },
          { label: 'Active Links', value: kpis.active_links || 0, icon: Lightning, color: 'text-amber-400' },
          { label: 'Conv. Rate', value: `${kpis.conversion_rate || 0}%`, icon: ArrowsClockwise, color: 'text-blue-400' },
          { label: 'Balance', value: `$${(kpis.balance || 0).toLocaleString()}`, icon: CurrencyDollar, color: 'text-emerald-400' },
        ].map((item, i) => (
          <Card key={i} className={`bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all animate-fade-in stagger-${i+1}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{item.label}</p>
                  <p className={`text-2xl font-bold tracking-tight mt-1 font-heading ${item.color}`}>{item.value}</p>
                </div>
                <item.icon size={24} className={`${item.color} opacity-50`} weight="duotone" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-zinc-900 border-zinc-800 animate-fade-in stagger-5">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg text-white">Earnings (7 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={earnings_chart || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#27272A' }}
                tickFormatter={v => v?.split('-').slice(1).join('/')} />
              <YAxis tick={{ fill: '#71717A', fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#27272A' }}
                tickFormatter={v => `$${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="revenue" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.08} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#3B82F6' }} name="revenue" />
              <Area type="monotone" dataKey="clicks" stroke="#10B981" fill="#10B981" fillOpacity={0.05} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: '#10B981' }} name="clicks" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 animate-fade-in stagger-6">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
            <Lightning size={20} className="text-amber-400" />
            Smart Links
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Offer</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Slug</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Clicks</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Conv.</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider">Revenue</TableHead>
                <TableHead className="text-zinc-500 text-xs uppercase tracking-wider"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(smart_links || []).map((link, i) => (
                <TableRow key={i} className="border-zinc-800/50" data-testid={`smart-link-${i}`}>
                  <TableCell className="text-zinc-200 text-sm max-w-[200px] truncate">{link.offer_title}</TableCell>
                  <TableCell className="text-blue-400 font-mono text-xs">{link.slug}</TableCell>
                  <TableCell className="text-zinc-300">{(link.clicks || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-zinc-300">{link.conversions || 0}</TableCell>
                  <TableCell className="text-emerald-400 font-mono">${(link.revenue || 0).toLocaleString()}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={() => copyLink(link.url)} data-testid={`copy-link-${i}`} className="text-zinc-400 hover:text-blue-400">
                      <Copy size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {(!smart_links || smart_links.length === 0) && (
                <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">No smart links yet. Apply for offers in the Marketplace.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
